import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuickActions } from '../contexts/QuickActionsContext';
import { 
  Upload,
  Plus,
  Buy,
  Discount,
  Wallet,
  Star,
  CloseSquare,
  MoreCircle,
  Document,
  ArrowUp,
  Message,
  Download
} from 'react-iconly';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};

const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Quick Actions configuration
const QUICK_ACTIONS = [
  {
    id: 'add-product',
    label: 'Add Product',
    icon: Plus,
    action: 'add-product'
  },

  {
    id: 'view-orders',
    label: 'View Orders',
    icon: Buy,
    action: 'view-orders'
  },
  {
    id: 'manage-discounts',
    label: 'Manage Discounts',
    icon: Discount,
    action: 'manage-discounts'
  },

  {
    id: 'connect-bank',
    label: 'Connect Bank Account',
    icon: Wallet,
    action: 'connect-bank'
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: Document,
    action: 'documents'
  },
  {
    id: 'legal-center',
    label: 'Legal Center',
    icon: Star,
    action: 'legal-center'
  },
          {
          id: 'financials',
          label: 'Financials',
          icon: Wallet,
          action: 'financials'
        },
        {
          id: 'conversions',
          label: 'Conversions',
          icon: ArrowUp,
          action: 'conversions'
        },
        {
          id: 'reviews',
          label: 'Reviews',
          icon: Star,
          action: 'reviews'
        },
        {
          id: 'messages',
          label: 'Messages',
          icon: Message,
          action: 'messages'
        },
        {
          id: 'qr-code',
          label: 'QR Code Generator',
          icon: Download,
          action: 'qr-code'
        }
];

export function SideNavigation({ onQuickAction, activeAction, storeId }) {
  const navigate = useNavigate();
  const { isQuickActionsOpen, isMobile, closeQuickActions } = useQuickActions();

  const handleQuickAction = (actionId) => {
    if (onQuickAction) {
      onQuickAction(actionId);
    }
    
    // Close mobile menu after action
    if (isMobile) {
      closeQuickActions();
    }
  };

  const handleActionClick = (action) => {
    switch (action) {
      case 'add-product':
        navigate('/add-product');
        break;
      case 'view-orders':
        navigate('/orders');
        break;
      case 'manage-discounts':
        navigate('/discounts');
        break;
      case 'connect-bank':
        navigate('/seller-hub/payments/bank');
        break;
      case 'documents':
        navigate('/documents');
        break;
      case 'legal-center':
        navigate('/legal-center');
        break;
      case 'financials':
        navigate('/seller-hub/financials');
        break;
      case 'conversions':
        navigate('/seller-hub/conversions');
        break;
      case 'reviews':
        if (storeId) {
          navigate(`/storefront/${storeId}/reviews`);
        } else {
          // No store ID available for reviews navigation
        }
        break;
      case 'messages':
        navigate('/seller-hub/messages');
        break;
      case 'qr-code':
        navigate('/seller-hub/qr-code');
        break;
      default:
        // Unknown action
    }
  };

  const QuickActionButton = ({ action, isActive }) => {
    const IconComponent = action.icon;
    
    return (
      <button
        onClick={() => {
          handleActionClick(action.action);
          handleQuickAction(action.id);
        }}
        style={{
          background: isActive ? PRIMARY_BROWN : NEUTRAL_WHITE,
          color: isActive ? NEUTRAL_WHITE : NEUTRAL_TEXT,
          border: 'none',
          borderRadius: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          width: '100%',
          textAlign: 'left',
          position: 'relative',
          ...bodyFont
        }}
        onMouseOver={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = NEUTRAL_BG;
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        onMouseOut={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = NEUTRAL_WHITE;
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <IconComponent 
          set="regular" 
          size={20} 
          primaryColor={isActive ? NEUTRAL_WHITE : PRIMARY_BROWN} 
        />
        <span style={{ 
          fontSize: 12, 
          fontWeight: 600,
          flex: 1
        }}>
          {action.label}
        </span>
        {isActive && (
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: NEUTRAL_WHITE,
            borderRadius: '0 2px 2px 0'
          }} />
        )}
      </button>
    );
  };

  return (
    <>
      {/* Desktop Side Navigation */}
      {isQuickActionsOpen && (
        <div className="hidden md:block" style={{
          position: 'fixed',
          left: 0,
          top: 72, // Below header
          width: 280,
          height: 'calc(100vh - 72px)',
          background: NEUTRAL_WHITE,
          borderRight: '1px solid #E0E0E0',
          padding: '24px 20px',
          overflowY: 'auto',
          zIndex: 50
        }}>

        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {QUICK_ACTIONS.map((action) => (
            <QuickActionButton
              key={action.id}
              action={action}
              isActive={activeAction === action.id}
            />
          ))}
        </div>
      </div>
      )}

      {/* Mobile Overlay Menu - Only shown when triggered externally */}
      {isQuickActionsOpen && isMobile && (
        <div className="md:hidden" style={{
          position: 'fixed',
          top: 72, // Start below header
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 999
        }}>
          <div style={{
            position: 'fixed',
            top: 72, // Start below header
            left: 0,
            width: 280,
            height: 'calc(100vh - 72px)', // Adjust height to account for header
            background: NEUTRAL_WHITE,
            padding: '24px 20px', // Reduced top padding since we're below header
            overflowY: 'auto',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
          }} onClick={(e) => e.stopPropagation()}>

            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {QUICK_ACTIONS.map((action) => (
                <QuickActionButton
                  key={action.id}
                  action={action}
                  isActive={activeAction === action.id}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
